import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API = `/api`;

const clientTypes = ['Artist', 'Brand', 'Agency', 'Label', 'Corporate'];
const statuses = ['Active', 'Inactive', 'Blacklisted'];

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  const [formData, setFormData] = useState({
    client_name: '',
    contact_person: '',
    email: '',
    phone: '',
    company_website: '',
    address: '',
    gstin: '',
    client_type: 'Artist',
    industry: '',
    status: 'Active',
    payment_terms: '',
    rating: 3,
    notes: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
    } catch {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = useMemo(() => {
    return clients.filter(c =>
      c.client_name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase())
    );
  }, [clients, search]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, formData);
        toast.success('Client updated');
      } else {
        await axios.post(`${API}/clients`, formData);
        toast.success('Client created');
      }

      handleCancel();
      fetchClients();
    } catch (err) {
      toast.error('Failed to save client');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client?')) return;
    await axios.delete(`${API}/clients/${id}`);
    fetchClients();
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData(client);
    setIsDialogOpen(true);
  };

  const handleCancel = () => {
    setEditingClient(null);
    setFormData({
      client_name: '',
      contact_person: '',
      email: '',
      phone: '',
      company_website: '',
      address: '',
      gstin: '',
      client_type: 'Artist',
      industry: '',
      status: 'Active',
      payment_terms: '',
      rating: 3,
      notes: ''
    });
    setIsDialogOpen(false);
  };

  if (loading) return <div className="text-center py-12">Loading clients...</div>;

  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <Input
          placeholder="Search clients..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="md:w-64"
        />

        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      {/* TABLE */}
      <div className="bg-app-card border border-app-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-app-muted text-app-subtext">
            <tr>
              <th className="px-4 py-3 text-left">Client</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Industry</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Revenue</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredClients.map(client => (
              <tr key={client.id} className="border-t border-app-border hover:bg-app-muted/40">
                <td className="px-4 py-3">
                  <div className="font-medium text-app-text">{client.client_name}</div>
                  <div className="text-xs text-app-subtext">{client.email}</div>
                </td>

                <td className="px-4 py-3">{client.client_type}</td>
                <td className="px-4 py-3">{client.industry || '-'}</td>

                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs ${
                    client.status === 'Active'
                      ? 'bg-green-500/10 text-green-600'
                      : client.status === 'Inactive'
                      ? 'bg-yellow-500/10 text-yellow-600'
                      : 'bg-red-500/10 text-red-600'
                  }`}>
                    {client.status}
                  </span>
                </td>

                <td className="px-4 py-3 font-semibold">
                  ₹{(client.total_revenue || 0).toLocaleString('en-IN')}
                </td>

                <td className="px-4 py-3 text-right space-x-2">
                  <button onClick={() => handleEdit(client)}>
                    <Edit className="w-4 h-4 text-blue-500 inline" />
                  </button>
                  <button onClick={() => handleDelete(client.id)}>
                    <Trash2 className="w-4 h-4 text-red-500 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredClients.length === 0 && (
          <div className="text-center py-8 text-app-subtext">
            No clients found.
          </div>
        )}
      </div>

      {/* DIALOG */}
      <Dialog open={isDialogOpen} onOpenChange={(o) => !o && handleCancel()}>
        <DialogContent className="bg-app-card border border-app-border max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {editingClient ? 'Edit Client' : 'New Client'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              <div>
                <Label>Client Name *</Label>
                <Input
                  required
                  value={formData.client_name}
                  onChange={(e) =>
                    setFormData({ ...formData, client_name: e.target.value })
                  }
                />
              </div>

              <div>
                <Label>Contact Person *</Label>
                <Input
                  required
                  value={formData.contact_person}
                  onChange={(e) =>
                    setFormData({ ...formData, contact_person: e.target.value })
                  }
                />
              </div>

              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>

              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                />
              </div>

              <div>
                <Label>Client Type</Label>
                <Select
                  value={formData.client_type}
                  onValueChange={(val) =>
                    setFormData({ ...formData, client_type: val })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {clientTypes.map(type => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(val) =>
                    setFormData({ ...formData, status: val })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map(s => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit">
                {editingClient ? 'Update' : 'Create'}
              </Button>
            </div>

          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Clients;